import { IntersectionIcon } from 'morpheus-ui';
import { Fragment, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useExamFeedbackHook } from '../../../../hooks/examFeedback';
import { IExercise } from '../../../../models/exercise';
import { Button } from '../../../atoms/Button';
import Container from '../../../atoms/Container';
import { Icon } from '../../../atoms/Icon';
import ExamFeedbackFooter from '../../../molecules/Exam/FeedbackFooter';
import { Typography } from '../../../molecules/Typography';
import { AnswerSheet } from '../../../organism/AnswerSheet';
import { ExamExerciseList } from '../../../organism/Exam/ExerciseList';
import ExamFeedbackStatus from '../../../organism/Exam/FeedbackStatus';
import { IPracticeEssay } from '../../../../models/essay';
import NewPrintComponent from '../../../organism/PrintComponent/newPrintComponent';
import SimuladoPrintModal, {
  SimuladoPrintItem,
} from '../../../organism/PrintComponent/SimuladoPrintModal';
import ExercisePaper from '../../PrintPaper/exercises';
import { usePrintComponentStore } from '../../../organism/PrintComponent/store/usePrintComponentStore';

interface ExamFeedbackProps {
  exercises: IExercise[];
  essay?: IPracticeEssay;
  correctAnswersPercentage: number;
  correctAnswersTotal: number;
  wrongAnswersTotal: number;
  type?: 'default' | 'enem';
  timeSpent: number | null;
  isLoading?: boolean;
  isPractice?: boolean;
  isMegaSimulation?: boolean;
  hasRevision?: boolean;
  /** Dia consultado, no formato de dois dias. */
  day?: 1 | 2 | null;
  examName?: string;
  /** PDFs diagramados cadastrados no admin, para o modal de impressão. */
  printableFiles?: SimuladoPrintItem[];
  onRemake: () => void;
  onRevision?: () => void;
  isEnemArea?:boolean
}

const ExamFeedback = ({
  exercises,
  essay,
  isLoading = false,
  correctAnswersPercentage,
  correctAnswersTotal,
  wrongAnswersTotal,
  timeSpent,
  type = 'default',
  isPractice = false,
  isMegaSimulation = false,
  hasRevision = true,
  day = null,
  examName,
  printableFiles = [],
  onRemake,
  onRevision = () => {},
  isEnemArea = false
}: ExamFeedbackProps) => {
  const { setData } = usePrintComponentStore();
  const { isOpenPrint, isOpenAnswerCard, setIsOpenPrint, setIsOpenAnswerCard } =
    useExamFeedbackHook();
  const [selectedQuestion, setSelectedQuestion] = useState<string>();
  // Fallback da impressão para simulados sem PDF diagramado cadastrado.
  const [useGeneratedPrint, setUseGeneratedPrint] = useState(false);

  // Processando as questões par
  const processedExercises = exercises?.map((exercise) => {
    if (exercise.alternatives) {
      const processedAlternatives = exercise.alternatives.map((alt) => {
        if (alt && alt.text) {
          let processedText = alt.text;

          processedText = processedText.replace(
            /{STORAGE_BASE_URL}/g,
            'https://storage.aprovatotal.com.br'
          );

          return { ...alt, text: processedText };
        }
        return alt;
      });
      return { ...exercise, alternatives: processedAlternatives };
    }
    return exercise;
  });

  const ModelTitle = () => {
    if (isPractice) {
      return 'Imprimir Simulado';
    }

    if (isMegaSimulation) {
      return 'Imprimir';
    }

    return 'Imprimir SSR';
  };

  return (
    <Container className='flex flex-col gap-xxxl'>
      <div className='flex flex-col gap-xl'>
        <div className='flex flex-col items-center justify-between rounded-md bg-color-lightest px-4 py-8 shadow-level-1 md:px-12 xl:flex-row'>
          <ExamFeedbackStatus
            essayStatus={essay?.id ? essay.status : undefined}
            correctAnswersPercentage={correctAnswersPercentage}
            correctAnswersTotal={correctAnswersTotal}
            wrongAnswersTotal={wrongAnswersTotal}
            timeSpent={timeSpent}
          />
          {type === 'default' && hasRevision && (
            <div className='mt-8 flex-col items-center gap-lg xs:flex lg:hidden'>
              <Typography
                size='small'
                weight='bold'
                className='text-center'
              >
                Os assuntos que você errou nesse simulado já entraram na sua
                lista de revisão. Confira!
              </Typography>
              <Button onClick={() => onRevision()}>
                Acessar lista de revisões
              </Button>
            </div>
          )}
        </div>
        <div className='flex justify-between'>
          {!isPractice && (
            <Button onClick={() => onRemake()} variant='text'>
              <Icon Icon='refresh' type='material' className='hidden md:flex' />
              Refazer simulado
            </Button>
          )}
          <Button onClick={() => setIsOpenAnswerCard(true)} variant='text'>
            <Icon Icon='apps' type='material' className='hidden md:flex' />
            Ver cartão resposta
          </Button>
          {!isEnemArea && (

            <Button
            onClick={() => {
              setData(processedExercises);
              setIsOpenPrint(true);
            }}
            variant='text'
            LeftIcon='print'
            className='hidden md:flex'
            >
            Imprimir simulado
          </Button>
          )}
        </div>
      </div>
      <div className='flex flex-col gap-xs'>
        <Typography size='h2' type='title'>
          Gabarito
        </Typography>
        <Typography>
          Se encontrar algum problema na correção deste simulado, salve o link
          ou tire um printscreen e nos envie. Verificaremos as questões
          respondidas e efetuaremos as devidas correções quando necessário.
        </Typography>
      </div>
      {!processedExercises ? (
        <Fragment>
          <ContentLoader
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 30'
          >
            <rect x='0' y='2' rx='2' ry='2' width='100' height='15' />
          </ContentLoader>
          <ContentLoader
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 80'
          >
            <rect x='0' y='2' rx='2' ry='2' width='100%' height='70' />
          </ContentLoader>
          {Array.from({ length: 5 }, (_, index) => (
            <ContentLoader
              key={`${index}_loader`}
              backgroundColor={'#a7adba'}
              foregroundColor={'#c0c5ce'}
              viewBox='0 0 500 24'
            >
              <rect x='0' y='2' rx='2' ry='2' width='100%' height='20' />
            </ContentLoader>
          ))}
        </Fragment>
      ) : (
        <div className='flex flex-col gap-xxl pb-20'>
          {type === 'enem' ? (
            <>
              <SimuladoPrintModal
                isOpen={isOpenPrint && !useGeneratedPrint}
                onClose={() => setIsOpenPrint(false)}
                title='Imprimir simulado'
                subtitle={examName}
                items={printableFiles}
                onFallbackPrint={() => setUseGeneratedPrint(true)}
              />
              <NewPrintComponent
                type='exercise'
                isOpen={isOpenPrint && useGeneratedPrint}
                onClose={() => {
                  setIsOpenPrint(false);
                  setUseGeneratedPrint(false);
                }}
                onCancel={() => setUseGeneratedPrint(false)}
                title={ModelTitle()}
                subtitle='Interdisciplinar'
              >
                <ExercisePaper
                  cols='split'
                  size='default'
                  color='interdisciplinar'
                  iconType='svg'
                  icon={IntersectionIcon}
                  title='Exercícios'
                  module='interdisciplinar'
                />
              </NewPrintComponent>
            </>
          ) : (
            <NewPrintComponent
              type='exercise'
              isOpen={isOpenPrint}
              onClose={() => setIsOpenPrint(false)}
              title={ModelTitle()}
              subtitle='Interdisciplinar'
            >
              <ExercisePaper
                cols='split'
                size='default'
                color='interdisciplinar'
                iconType='svg'
                icon={IntersectionIcon}
                title='Exercícios'
                module='interdisciplinar'
              />
            </NewPrintComponent>
          )}

          <div className='flex flex-col items-center justify-between gap-xxl'>
            <ExamExerciseList
              data={processedExercises}
              isLoading={isLoading}
              showExerciseType={false}
              showExerciseArea
              showSubject
              onExerciseChange={() => {}}
              selectedId={selectedQuestion}
              orderBy={type === 'enem' ? 'order' : 'area'}
              clearSelected={() => {
                setSelectedQuestion(undefined);
                setIsOpenAnswerCard(false);
              }}
            />
          </div>
        </div>
      )}
      <AnswerSheet
        essay={essay ? { essay: essay, origin: 'feedback' } : undefined}
        isOpen={isOpenAnswerCard}
        answers={processedExercises}
        buttonText='Fechar'
        orderBy={type === 'enem' ? 'order' : 'area'}
        onClose={() => setIsOpenAnswerCard(false)}
        onClick={() => setIsOpenAnswerCard(false)}
        onSelect={(id: string) => setSelectedQuestion(id)}
      />
      {type === 'default' && hasRevision && (
        <ExamFeedbackFooter onRevisionListClick={() => onRevision()} />
      )}
    </Container>
  );
};

export default ExamFeedback;
